// Designer panels for live operational screens: a data source's live refresh,
// a repeat's board / positioned layout and selection, a block's conditional
// style rules, and the page's terminal (kiosk) mode. Kept out of the 5k-line
// PropertyPanel; each is mounted there next to the setting it extends.

import { Plus, Trash2 } from 'lucide-react'
import { FIELD_LABEL, INPUT } from './blockEditors'
import type {
  Block, TimerBlock, BlockRepeat, BlockStyle, BlockStyleRule, DataSourceRefresh, PageAction, PageDataSource,
  PageModel, PageTerminal, RepeatLane, RepeatLayout,
} from './types'

const HINT: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.5 }
const ROW: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 6 }
const BOX: React.CSSProperties = {
  marginTop: 8, padding: 8, borderRadius: 6, border: '1px dashed var(--color-border)',
  display: 'flex', flexDirection: 'column', gap: 6,
}
const CHECK: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }

function num(v: string): number | undefined {
  const t = v.trim()
  if (!t) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

function ActionSelect({ value, actions, onChange, empty = '— none —' }: {
  value?: string; actions: PageAction[]; onChange: (v: string | undefined) => void; empty?: string
}) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value || undefined)} style={INPUT}>
      <option value="">{empty}</option>
      {value && !actions.some((a) => a.name === value) && <option value={value}>{value} (not defined)</option>}
      {actions.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
    </select>
  )
}

// ─── Data source → live refresh ─────────────────────────────────────

export function DataSourceRefreshEditor({ source, actions, onChange }: {
  source: PageDataSource
  actions: PageAction[]
  onChange: (refresh: DataSourceRefresh | undefined) => void
}) {
  const r = source.refresh
  const on = !!r
  const live = source.bindingTime === 'runtime'
  const nr = r?.onNewRows || {}
  const set = (patch: Partial<DataSourceRefresh>) => onChange({ ...(r || {}), ...patch })
  const setNew = (patch: Partial<NonNullable<DataSourceRefresh['onNewRows']>>) => {
    const next = { ...nr, ...patch }
    set({ onNewRows: Object.values(next).some((v) => v !== undefined) ? next : undefined })
  }
  return (
    <div style={{ marginTop: 6 }}>
      <label style={CHECK}>
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => onChange(e.target.checked ? { intervalSeconds: 15 } : undefined)}
        />
        Keep live while the page is open
      </label>
      {on && !live && (
        <div style={{ ...HINT, color: 'var(--color-error)' }}>
          Live refresh needs <b>Binding time → Runtime</b>; a deployment source is fixed at publish.
        </div>
      )}
      {on && (
        <div style={BOX}>
          <div style={ROW}>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Every (seconds)</label>
              <input type="number" min={5} value={r?.intervalSeconds ?? 15}
                onChange={(e) => set({ intervalSeconds: Math.max(5, num(e.target.value) ?? 15) })} style={INPUT} />
            </span>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Row identity column</label>
              <input value={r?.keyColumn || ''} placeholder="_txn_id"
                onChange={(e) => set({ keyColumn: e.target.value.trim() || undefined })}
                style={{ ...INPUT, fontFamily: 'monospace' }} />
            </span>
          </div>
          <label style={{ ...FIELD_LABEL, marginTop: 4 }}>When new rows arrive</label>
          <div style={ROW}>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Highlight (seconds)</label>
              <input type="number" min={0} value={nr.highlightSeconds ?? 6}
                onChange={(e) => setNew({ highlightSeconds: Math.max(0, num(e.target.value) ?? 0) })} style={INPUT} />
            </span>
            <span style={{ flex: 1.4 }}>
              <label style={FIELD_LABEL}>Run page action</label>
              <ActionSelect value={nr.action} actions={actions} onChange={(action) => setNew({ action })} />
            </span>
          </div>
          <label style={CHECK}>
            <input type="checkbox" checked={!!nr.chime} onChange={(e) => setNew({ chime: e.target.checked || undefined })} />
            Play a chime
          </label>
          <div style={HINT}>
            Polls only while the tab is visible; a failed read keeps what is shown. The first read is a
            baseline — opening the screen never chimes. The action sees <code>{'{{new_rows}}'}</code>.
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Repeat → board / positioned layout + selection ─────────────────

export function RepeatLayoutExtras({ block, rep, page, onChange }: {
  block: Block
  rep: BlockRepeat
  page: PageModel
  onChange: (b: Block) => void
}) {
  const layout: RepeatLayout = rep.layout || {}
  const actions = page.actions || []
  const setLayout = (patch: Partial<RepeatLayout>) =>
    onChange({ ...block, repeat: { ...rep, layout: { ...layout, ...patch } } } as Block)
  const lanes = layout.lanes || []
  const setLanes = (next: RepeatLane[]) => setLayout({ lanes: next.length ? next : undefined })
  return (
    <>
      {layout.mode === 'board' && (
        <div style={BOX}>
          <div style={ROW}>
            <span style={{ flex: 1.3 }}>
              <label style={FIELD_LABEL}>Lane by column</label>
              <input value={layout.groupBy || ''} placeholder="e.g. STATUS"
                onChange={(e) => setLayout({ groupBy: e.target.value.trim() || undefined })}
                style={{ ...INPUT, fontFamily: 'monospace' }} />
            </span>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Lane width (px)</label>
              <input type="number" min={160} value={layout.laneWidth ?? ''} placeholder="280"
                onChange={(e) => setLayout({ laneWidth: num(e.target.value) })} style={INPUT} />
            </span>
          </div>
          <label style={FIELD_LABEL}>Lanes (empty = one per value, in row order)</label>
          {lanes.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 4 }}>
              <input value={l.value} placeholder="value"
                onChange={(e) => setLanes(lanes.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))}
                style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }} />
              <input value={l.label || ''} placeholder="label"
                onChange={(e) => setLanes(lanes.map((x, k) => (k === i ? { ...x, label: e.target.value || undefined } : x)))}
                style={{ ...INPUT, flex: 1.2 }} />
              <input type="color" value={l.color || '#2563eb'} title="Lane colour"
                onChange={(e) => setLanes(lanes.map((x, k) => (k === i ? { ...x, color: e.target.value } : x)))}
                style={{ width: 34, padding: 0, border: 'none', background: 'transparent' }} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLanes(lanes.filter((_, k) => k !== i))}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
            onClick={() => setLanes([...lanes, { value: '' }])}><Plus size={12} /> Add lane</button>
          <div>
            <label style={FIELD_LABEL}>Drag a card to another lane runs</label>
            <ActionSelect value={layout.dropAction} actions={actions} onChange={(dropAction) => setLayout({ dropAction })}
              empty="— no dragging —" />
            <div style={HINT}>
              The card's row is in scope (<code>{`{{${rep.as || 'item'}._txn_id}}`}</code>) and{' '}
              <code>{'{{drop.lane}}'}</code> is the lane it was dropped on. Lane header blocks
              (<code>layout.laneHeader</code>) read <code>{'{{lane.value}}'}</code>, <code>{'{{lane.count}}'}</code>,{' '}
              <code>{'{{lane.ids}}'}</code> and the lane's first row.
            </div>
          </div>
        </div>
      )}
      {layout.mode === 'positioned' && (
        <div style={BOX}>
          <div style={ROW}>
            {(['x', 'y', 'w', 'h'] as const).map((k) => (
              <span key={k} style={{ flex: 1 }}>
                <label style={FIELD_LABEL}>{k.toUpperCase()} column</label>
                <input value={layout[k] || ''} placeholder={k === 'w' ? '100' : k === 'h' ? '80' : `POS_${k.toUpperCase()}`}
                  onChange={(e) => setLayout({ [k]: e.target.value.trim() || undefined } as Partial<RepeatLayout>)}
                  style={{ ...INPUT, fontFamily: 'monospace' }} />
              </span>
            ))}
          </div>
          <div style={ROW}>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Plane width</label>
              <input type="number" min={1} value={layout.canvasWidth ?? ''} placeholder="1000"
                onChange={(e) => setLayout({ canvasWidth: num(e.target.value) })} style={INPUT} />
            </span>
            <span style={{ flex: 1 }}>
              <label style={FIELD_LABEL}>Plane height</label>
              <input type="number" min={1} value={layout.canvasHeight ?? ''} placeholder="600"
                onChange={(e) => setLayout({ canvasHeight: num(e.target.value) })} style={INPUT} />
            </span>
            <span style={{ flex: 1.2 }}>
              <label style={FIELD_LABEL}>On small screens</label>
              <select value={layout.mobile || 'scale'} onChange={(e) => setLayout({ mobile: e.target.value as 'scale' | 'grid' })} style={INPUT}>
                <option value="scale">Scale the plane</option>
                <option value="grid">Fall back to a grid</option>
              </select>
            </span>
          </div>
          <div style={HINT}>
            Each copy sits at its row's own coordinates in plane units (a column name, or a fixed number
            for W / H). The plane scales with the page width.
          </div>
        </div>
      )}
    </>
  )
}

export function RepeatSelectEditor({ block, rep, page, onChange }: {
  block: Block
  rep: BlockRepeat
  page: PageModel
  onChange: (b: Block) => void
}) {
  const sel = rep.select
  const vars = (page.inputs || []).map((i) => i.name)
  const set = (next: BlockRepeat['select']) => onChange({ ...block, repeat: { ...rep, select: next } } as Block)
  return (
    <div style={{ marginTop: 8 }}>
      <label style={FIELD_LABEL}>Tap to select</label>
      <select
        value={sel?.mode || ''}
        onChange={(e) => {
          const mode = e.target.value as '' | 'single' | 'multi'
          set(mode ? { mode, var: sel?.var || '', key: sel?.key } : undefined)
        }}
        style={INPUT}
      >
        <option value="">Off</option>
        <option value="single">One at a time</option>
        <option value="multi">Many</option>
      </select>
      {sel && (
        <div style={ROW}>
          <span style={{ flex: 1.2 }}>
            <label style={FIELD_LABEL}>Store in variable</label>
            <input list="pg-select-vars" value={sel.var} placeholder="selected_ids"
              onChange={(e) => set({ ...sel, var: e.target.value.trim() })}
              style={{ ...INPUT, fontFamily: 'monospace', borderColor: sel.var ? undefined : 'var(--color-error)' }} />
            <datalist id="pg-select-vars">{vars.map((v) => <option key={v} value={v} />)}</datalist>
          </span>
          <span style={{ flex: 1 }}>
            <label style={FIELD_LABEL}>Key column</label>
            <input value={sel.key || ''} placeholder="_txn_id"
              onChange={(e) => set({ ...sel, key: e.target.value.trim() || undefined })}
              style={{ ...INPUT, fontFamily: 'monospace' }} />
          </span>
        </div>
      )}
      {sel && (
        <div style={HINT}>
          The variable holds the chosen keys (<code>{`{{${sel.var || 'var'}|count}}`}</code> counts them) — hand it to a{' '}
          <b>Run a transaction action</b> step's ids. A copy reads <code>{`{{${rep.as || 'item'}._selected}}`}</code>.
          Keys whose rows leave the source drop out of the selection.
        </div>
      )}
    </div>
  )
}

// ─── Block → conditional style rules ────────────────────────────────

export function StyleRulesEditor({ value, onChange }: {
  value: BlockStyleRule[] | undefined
  onChange: (next: BlockStyleRule[] | undefined) => void
}) {
  const rules = value || []
  const setAt = (i: number, r: BlockStyleRule) => onChange(rules.map((x, k) => (k === i ? r : x)))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rules.map((r, i) => {
        const st: BlockStyle = r.style || {}
        const setStyle = (patch: Partial<BlockStyle>) => {
          const next: BlockStyle = { ...st, ...patch }
          for (const k of Object.keys(next) as (keyof BlockStyle)[]) if (next[k] === undefined) delete next[k]
          setAt(i, { ...r, style: next })
        }
        const bg = st.background?.kind === 'color' ? st.background.color || '' : ''
        return (
          <div key={i} style={BOX}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span style={{ flex: 1 }}>
                <label style={FIELD_LABEL}>When</label>
                <input value={r.when} placeholder="{{item.FIRED_AT|age_min}} > {{item.TARGET_MIN}}"
                  onChange={(e) => setAt(i, { ...r, when: e.target.value })}
                  style={{ ...INPUT, fontFamily: 'monospace' }} />
              </span>
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 18 }}
                onClick={() => { const next = rules.filter((_, k) => k !== i); onChange(next.length ? next : undefined) }}>
                <Trash2 size={12} />
              </button>
            </div>
            <div style={ROW}>
              <ColorField label="Background" value={bg}
                onChange={(c) => setStyle({ background: c ? { kind: 'color', color: c } : undefined })} />
              <ColorField label="Text" value={st.textColor || ''} onChange={(c) => setStyle({ textColor: c || undefined })} />
              <ColorField label="Border" value={st.border?.color || ''}
                onChange={(c) => setStyle({ border: c ? { width: st.border?.width || 2, color: c, style: st.border?.style } : undefined })} />
              <span style={{ flex: 0.8 }}>
                <label style={FIELD_LABEL}>Opacity</label>
                <input type="number" min={0} max={1} step={0.1} value={st.opacity ?? ''}
                  onChange={(e) => setStyle({ opacity: num(e.target.value) })} style={INPUT} />
              </span>
            </div>
            <SettingRows value={r.set} onChange={(set) => setAt(i, { ...r, set })} />
          </div>
        )
      })}
      <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        onClick={() => onChange([...rules, { when: '', style: {} }])}>
        <Plus size={12} /> Add rule
      </button>
      <div style={HINT}>
        Rules that hold layer their style over the block's own, in order (a later rule wins) — same syntax as
        a visibility condition, and inside a repeat they read the row. Settings change the block itself (a card's
        surface is <code>card.background</code>). <code>{'{{col|age_min}}'}</code> is minutes since a time
        (re-checked as the clock moves); a token that does not resolve leaves the rule off.
      </div>
    </div>
  )
}

/** A rule's `set`: block settings (dotted paths) overridden while it holds —
 *  for what the wrapper style cannot reach, e.g. a card's own surface. */
function SettingRows({ value, onChange }: {
  value: Record<string, unknown> | undefined
  onChange: (next: Record<string, unknown> | undefined) => void
}) {
  const entries = Object.entries(value || {})
  const write = (next: [string, unknown][]) => {
    const obj = Object.fromEntries(next)
    onChange(Object.keys(obj).length ? obj : undefined)
  }
  return (
    <div>
      <label style={FIELD_LABEL}>…and set block settings</label>
      {entries.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <input value={k} placeholder="card.background"
            onChange={(e) => write(entries.map(([kk, vv], j) => (j === i ? [e.target.value, vv] : [kk, vv])))}
            style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }} />
          <input value={String(v ?? '')} placeholder="#fde2e1"
            onChange={(e) => write(entries.map(([kk, vv], j) => (j === i ? [kk, e.target.value] : [kk, vv])))}
            style={{ ...INPUT, flex: 1 }} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => write(entries.filter((_, j) => j !== i))}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 4 }}
        onClick={() => write([...entries, ['', '']])}><Plus size={12} /> Add setting</button>
    </div>
  )
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (c: string) => void }) {
  return (
    <span style={{ flex: 1 }}>
      <label style={FIELD_LABEL}>{label}</label>
      <span style={{ display: 'flex', gap: 4 }}>
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 30, padding: 0, border: 'none', background: 'transparent' }} />
        <input value={value} placeholder="none" onChange={(e) => onChange(e.target.value.trim())}
          style={{ ...INPUT, fontFamily: 'monospace', minWidth: 0 }} />
      </span>
    </span>
  )
}

// ─── Page → terminal (kiosk) mode ───────────────────────────────────

export function TerminalEditor({ value, onChange }: {
  value: PageTerminal | undefined
  onChange: (next: PageTerminal | undefined) => void
}) {
  const t = value || {}
  const set = (patch: Partial<PageTerminal>) => {
    const next = { ...t, ...patch }
    onChange(next.fullscreen || next.wakeLock || next.sound ? next : undefined)
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={CHECK}>
        <input type="checkbox" checked={!!t.fullscreen} onChange={(e) => set({ fullscreen: e.target.checked || undefined })} />
        Full screen
      </label>
      <label style={CHECK}>
        <input type="checkbox" checked={!!t.wakeLock} onChange={(e) => set({ wakeLock: e.target.checked || undefined })} />
        Keep the screen awake
      </label>
      <label style={CHECK}>
        <input type="checkbox" checked={!!t.sound} onChange={(e) => set({ sound: e.target.checked || undefined })} />
        Allow sound (arrival chimes)
      </label>
      {value && (
        <div>
          <label style={FIELD_LABEL}>Start button caption</label>
          <input value={t.startLabel || ''} placeholder="Start"
            onChange={(e) => set({ startLabel: e.target.value || undefined })} style={INPUT} />
        </div>
      )}
      <div style={HINT}>
        For an always-on screen (a kitchen display, a queue board). Browsers allow full screen and sound only
        after a tap, so the page opens with a one-tap Start. Live surfaces only — a published static page ignores it.
      </div>
    </div>
  )
}

// ─── Timer block ────────────────────────────────────────────────────

export function TimerEditor({ block, onChange }: { block: TimerBlock; onChange: (b: Block) => void }) {
  const set = (patch: Partial<TimerBlock>) => onChange({ ...block, ...patch } as Block)
  const text = (k: 'from' | 'until' | 'warnAfter' | 'dangerAfter' | 'label', label: string, placeholder: string) => (
    <span style={{ flex: 1 }}>
      <label style={FIELD_LABEL}>{label}</label>
      <input value={(block[k] as string) || ''} placeholder={placeholder}
        onChange={(e) => set({ [k]: e.target.value || undefined } as Partial<TimerBlock>)}
        style={{ ...INPUT, fontFamily: k === 'label' ? undefined : 'monospace' }} />
    </span>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {text('from', 'Counts from', '{{item.CREATED_AT}}')}
      {text('until', 'Stops at (optional)', '{{item.READY_AT}}')}
      <div style={ROW}>
        {text('warnAfter', 'Amber after (min)', '10 or {{item.WARN_MIN}}')}
        {text('dangerAfter', 'Red after (min)', '20 or {{item.TARGET_MIN}}')}
      </div>
      <div style={ROW}>
        {text('label', 'Caption', 'Waiting')}
        <span style={{ flex: 1 }}>
          <label style={FIELD_LABEL}>Format</label>
          <select value={block.format || 'auto'} onChange={(e) => set({ format: e.target.value as TimerBlock['format'] })} style={INPUT}>
            <option value="auto">m:ss, then h:mm:ss</option>
            <option value="mm:ss">minutes:seconds</option>
            <option value="h:mm">hours:minutes</option>
          </select>
        </span>
        <span style={{ flex: 0.8 }}>
          <label style={FIELD_LABEL}>Size</label>
          <select value={block.textSize || 'md'} onChange={(e) => set({ textSize: e.target.value as TimerBlock['textSize'] })} style={INPUT}>
            <option value="sm">Small</option>
            <option value="md">Medium</option>
            <option value="lg">Large</option>
          </select>
        </span>
      </div>
      <div style={HINT}>
        Ticks every second on a live page; the static site shows the value at publish. Inside a repeat, point
        the fields at the row so each copy times its own record and takes its own thresholds.
      </div>
    </div>
  )
}
